import { Client, cacheExchange, fetchExchange } from '@urql/core';
import { PalletSolutionGroup } from '@shared/types/entities/solution-group';
import { graphql } from './gql';
import { WorkerApi } from '@shared/types/api';
import { ClaimLower, Transaction, TransactionType } from '@shared/types/entities/transaction';
import Web3 from 'web3';

export class CoreIndexer {
  private client: Client;
  public readonly url: string;
  public readonly logger: typeof console;

  constructor(url: string, logger: typeof console = console) {
    this.url = url;
    this.logger = logger;
    this.client = new Client({
      url: url,
      exchanges: [cacheExchange, fetchExchange],
      requestPolicy: 'network-only',
    });
  }

  getSubscriptionsByOperator = async (
    operatorId: string,
  ): Promise<
    {
      group: WorkerApi.GetSolutionGroupSubscriptions[number]['group'];
      solutions: (WorkerApi.GetSolutionGroupSubscriptions[number]['solutions'][number] & {
        expirationBlock: string;
      })[];
      stakes: {
        currentStake: string;
        nextStake: string;
        rewardPeriodIndex: number;
        namespace: string;
      }[];
      unsubscribeBlock: number;
    }[]
  > => {
    const query = graphql(`
      query SubscriptionsByOperator($id: String!) {
        operatorAccountById(id: $id) {
          subscribedStakes(orderBy: rewardPeriodIndex_DESC) {
            currentStake
            nextStake
            rewardPeriodIndex
            solutionGroup {
              namespace
            }
          }
          subscriptions {
            id
            namespace

            solutionGroup {
              id
              namespace
              operationStartBlock
              operationEndBlock
              withdrawalDelay
              operatorsConfig {
                startBlock
                allowedOperators
                maxOperatorWorkers
                startBlock
                stakingAmounts {
                  min
                  max
                }
                hasOperatorsAllowlist
              }
              rewardsConfig {
                subscriptionRewardPerBlock
                votingRewardPerBlock
                topPerformanceBonus
              }
              info {
                id
                name
                description
                publisherInfo
                logoUrl
              }
              solutions {
                id
                status
                namespace
                additionToExtraneousGroupsAllowed
                expirationBlock
                workload {
                  workLogic
                  executionEnvironment
                }
                expirationBlock
                info {
                  id
                  description
                }
              }
              scheduledUnsubscriptions(
                orderBy: unsubscribeBlock_DESC_NULLS_LAST
                where: { operator: { id_eq: $id } }
                limit: 1
              ) {
                unsubscribeBlock
              }
            }
          }
        }
      }
    `);
    const result = await this.client.query(query, { id: operatorId });
    const stakes = (result.data?.operatorAccountById?.subscribedStakes ?? []).map(
      ({ currentStake, nextStake, rewardPeriodIndex, solutionGroup }) => ({
        currentStake,
        nextStake,
        rewardPeriodIndex,
        namespace: solutionGroup?.namespace ?? '',
      }),
    );

    // @ts-ignore
    return (result.data?.operatorAccountById?.subscriptions ?? []).map((subscription) => {
      let unsubscribeBlock = 0;
      if (subscription.solutionGroup.scheduledUnsubscriptions.length) {
        unsubscribeBlock = subscription.solutionGroup.scheduledUnsubscriptions[0].unsubscribeBlock;
      }

      // TODO: Use the correct solutions data
      const solutions = subscription.solutionGroup.solutions.map(
        ({ workload: { executionEnvironment, workLogic }, id, status, expirationBlock }) => ({
          flowSource: '',
          workLogic,
          // TODO: for some reason type requires this to be number but is a string
          id: id as unknown as number,
          creationDate: new Date(),
          environment: executionEnvironment,
          isDeleted: false,
          isExpired: false,
          actionStatus: 'NoAction',
          actionRetry: 0,
          isHadDeniedNode: false,
          downloadPath: null,
          updatedDate: null,
          status: status ?? null,
          statusDate: null,
          noderedId: null,
          ewxAddress: null,
          subcribeId: null,
          solutionId: null,
          actionErrorMsg: null,
          expirationBlock,
        }),
      );

      return {
        group: {
          Solution: solutions,
          id: subscription.solutionGroup.id,
          updatedDate: new Date(),
          creationDate: new Date(),
          activeParticipationAmount: subscription.solutionGroup.rewardsConfig.votingRewardPerBlock,
          allowedOperators: subscription.solutionGroup.operatorsConfig.allowedOperators.toString(),
          description: subscription.solutionGroup.info.description,
          environment: '',
          hasOperatorsAllowlist: subscription.solutionGroup.operatorsConfig.hasOperatorsAllowlist,
          isDeleted: false,
          isExpired: false,
          logoUrl: subscription.solutionGroup.info.logoUrl ?? '',
          maxOperatorWorkers:
            subscription.solutionGroup.operatorsConfig.maxOperatorWorkers.toString(),
          name: subscription.solutionGroup.info.name,
          namespace: subscription.solutionGroup.namespace,
          operationEndBlock: subscription.solutionGroup.operationEndBlock.toString(),
          operationEndBlockTimestamp: '',
          operationStartBlock: subscription.solutionGroup.operationStartBlock.toString(),
          operationStartBlockTimestamp: '',
          publisherInfo: subscription.solutionGroup.info.publisherInfo,
          stakingMax: subscription.solutionGroup.operatorsConfig.stakingAmounts.max,
          stakingMin: subscription.solutionGroup.operatorsConfig.stakingAmounts.min,
          startBlock: subscription.solutionGroup.operatorsConfig.startBlock.toString(),
          startBlockTimestamp: '',
          subscriptionRewardAmount:
            subscription.solutionGroup.rewardsConfig.subscriptionRewardPerBlock, // ??
          subscriptionRewardPerBlock:
            subscription.solutionGroup.rewardsConfig.subscriptionRewardPerBlock,
          topPerformanceBonus: subscription.solutionGroup.rewardsConfig.topPerformanceBonus,
          votingRewardPerBlock: subscription.solutionGroup.rewardsConfig.votingRewardPerBlock,
          withdrawalDelay: subscription.solutionGroup.withdrawalDelay ?? 0,
        },
        solutions,
        stakes,
        unsubscribeBlock,
      };
    });
  };

  getSubscriptionStake = async ({
    operatorId,
    groupId,
  }: {
    operatorId: string;
    groupId: string;
  }) => {
    const currentRewardPeriod = await this.getCurrentRewardPeriod();
    const queryId = `${operatorId}-${groupId}-${currentRewardPeriod}`;
    const query = graphql(`
      query GetSubscriptionStake($queryId: String!) {
        operatorSubscribedStakeById(id: $queryId) {
          currentStake
          nextStake
        }
      }
    `);
    const result = await this.client.query(query, { queryId });

    if (!result.data || !result.data.operatorSubscribedStakeById) {
      throw new Error('Failed to fetch subscription stake');
    }

    const { currentStake, nextStake } = result.data.operatorSubscribedStakeById;

    if (!currentStake) {
      throw new Error('Current stake value is not defined');
    }

    if (!nextStake) {
      throw new Error('Next stake value is not defined');
    }

    const floatingStakeAmount =
      BigInt((nextStake || 0).toString()) - BigInt((currentStake || 0).toString());

    const solutionGroupStake = {
      period: currentRewardPeriod.toString(),
      stakeAmount: currentStake,
      floatingStakeAmount: floatingStakeAmount.toString(),
    };

    return solutionGroupStake;
  };

  getSubscriptionsStake = async (operatorId: string) => {
    const currentRewardPeriod = await this.getCurrentRewardPeriod();
    const query = graphql(`
      query GetSubscriptionsStake($operatorId: String!, $currentRewardPeriod: Int!) {
        operatorSubscribedSolutionGroups(where: { operator: { id_eq: $operatorId } }) {
          solutionGroup {
            id
            stakes(
              where: {
                operator: { id_eq: $operatorId }
                rewardPeriodIndex_eq: $currentRewardPeriod
              }
            ) {
              currentStake
              nextStake
            }
          }
        }
      }
    `);

    const result = await this.client.query(query, {
      operatorId,
      currentRewardPeriod,
    });

    if (!result.data || !result.data.operatorSubscribedSolutionGroups) {
      throw new Error(
        "Failed to fetch subscriptions stake: 'operatorSubscribedSolutionGroups' property not found",
      );
    }

    const stakesData: WorkerApi.StakeGroupAmount[] = result.data.operatorSubscribedSolutionGroups
      .filter((subscription) => subscription.solutionGroup.stakes.length > 0)
      .map((subscription) => {
        const { solutionGroup } = subscription;
        const { stakes } = solutionGroup;
        const { currentStake, nextStake } = stakes[0];

        if (!solutionGroup || !stakes) {
          throw new Error('Solution group not found');
        }

        const floatingStakeAmount =
          BigInt((nextStake || 0).toString()) - BigInt((currentStake || 0).toString());

        return {
          groupId: solutionGroup.id,
          amount: currentStake,
          floatingAmount: floatingStakeAmount.toString(),
          isReadyToVote: +currentStake !== 0,
        };
      });

    return stakesData;
  };

  getEarnedRewards = async ({
    operatorId,
    groupNamespace,
  }: {
    operatorId: string;
    groupNamespace: string;
  }) => {
    const query = graphql(`
      query GetEarnedRewards($operatorId: String!, $groupNamespace: String!) {
        earnedRewards(
          where: {
            account: { id_eq: $operatorId }
            AND: { solutionGroup: { namespace_eq: $groupNamespace } }
          }
        ) {
          id
          subscriptionReward
          participationReward
        }
      }
    `);
    const result = await this.client.query(query, { operatorId, groupNamespace });

    return result.data?.earnedRewards ?? [];
  };

  getLowersBySender = async (senderAddress: string): Promise<Transaction[]> => {
    const query = graphql(`
      query Lowers($sender: String) {
        avnLowers(where: { sender: { id_eq: $sender } }, orderBy: lowerId_DESC) {
          id
          when
          amount
          blockNumber
          sender {
            id
          }
          receiver
          isPending
          isFinalized
          encodedLowerProof
          lowerPendingProof
          readyToBeClaimed
          claimed
          claimBlockNumber
          lowerId
          target
        }
      }
    `);
    const result = await this.client.query(query, { sender: senderAddress });
    return (result.data?.avnLowers ?? []).map((lower) => ({
      txHash: '',
      amount: Number(Web3.utils.fromWei(lower.amount!, 'ether')),
      id: lower.id,
      addressEWX: lower.sender.id!,
      addressEWC: lower.receiver!,
      block: lower.blockNumber,
      type: TransactionType.LOWERING,
      when: lower.when,
      isPending: lower.isPending ?? null,
      isFinalized: lower.isFinalized ?? null,
      lowerId: lower.lowerId ?? null,
      lowerPendingProof: lower.lowerPendingProof ?? null,
      encodedLowerProof: lower.encodedLowerProof ?? null,
      readyToBeClaimed: lower.readyToBeClaimed ?? null,
      claimed: lower.claimed ?? null,
      claimBlockNumber: lower.claimBlockNumber ?? null,
      target: lower.target ?? null,
    }));
  };

  getLiftsByReceiver = async (receiverAddress: string): Promise<Transaction[]> => {
    const query = graphql(`
      query Lifts($receiver: String) {
        avnLifts(where: { receiver: { id_eq: $receiver } }) {
          id
          when
          amount
          blockNumber
          ewcTxHash
          sender
          receiver {
            id
          }
        }
      }
    `);
    const result = await this.client.query(query, { receiver: receiverAddress });
    return (result.data?.avnLifts ?? []).map((lift) => ({
      txHash: lift.ewcTxHash!,
      amount: Number(Web3.utils.fromWei(lift.amount!, 'ether')),
      id: lift.id,
      addressEWX: lift.receiver!.id,
      addressEWC: lift.sender!,
      block: lift.blockNumber,
      type: TransactionType.LIFTING,
      when: lift.when,
      isPending: null,
      isFinalized: null,
    }));
  };

  getPermitLiftAverageGasFee = async (): Promise<number> => {
    const query = graphql(`
      query GetPermitLiftAverageGasFee {
        permitLiftGasFees {
          averageGasFee
        }
      }
    `);

    const result = await this.client.query(query, {});
    const arrayData = result.data?.permitLiftGasFees;

    if (!arrayData) {
      return 0;
    }

    return arrayData[0].averageGasFee;
  };

  getClaimLowers = async (addressEWX: string): Promise<ClaimLower[]> => {
    const query = graphql(`
      query ClaimLowers($sender: String) {
        avnLowers(where: { sender: { id_eq: $sender } }, orderBy: lowerId_DESC) {
          isFinalized
          isPending
          lowerId
          amount
          sender {
            id
          }
          receiver
          id
          when
          lowerPendingProof
          readyToBeClaimed
          claimed
          blockNumber
          claimBlockNumber
        }
      }
    `);
    const result = await this.client.query(query, { sender: addressEWX });
    console.log(
      'Received claim lowers',
      result.data?.avnLowers.map((lower) => lower.receiver),
    );
    return (result.data?.avnLowers ?? []).map((lower) => ({
      txHash: '',
      amount: Number(Web3.utils.fromWei(lower.amount!, 'ether')),
      id: lower.id,
      addressEWX: lower.sender.id!,
      addressEWC: lower.receiver!,
      block: lower.blockNumber,
      type: TransactionType.CLAIM_LOWERS,
      when: lower.when,
      isPending: lower.isPending ?? null,
      isFinalized: lower.isFinalized ?? null,
      lowerPendingProof: lower.lowerPendingProof ?? null,
      readyToBeClaimed: lower.readyToBeClaimed ?? null,
      claimed: lower.claimed ?? null,
      claimBlockNumber: lower.claimBlockNumber ?? null,
    }));
  };

  getSolutionsGroups = async (): Promise<PalletSolutionGroup[]> => {
    const query = graphql(`
      query GetSolutionsGroups {
        solutionGroups {
          id
          withdrawalDelay
          operationStartBlock
          operationEndBlock
          namespace
          info {
            description
            id
            logoUrl
            name
            publisherInfo
          }
          operatorsConfig {
            allowedOperators
            hasOperatorsAllowlist
            id
            maxOperatorWorkers
            stakingAmounts {
              id
              max
              min
            }
            startBlock
          }
          registrar {
            friendlyName
            id
            isAllowed
            legalLocation
          }
          rewardsConfig {
            id
            subscriptionRewardPerBlock
            topPerformanceBonus
            votingRewardPerBlock
          }
        }
      }
    `);

    const result = await this.client.query(query, {});

    if (!result.data) {
      throw new Error('Failed to fetch solution groups');
    }

    const solutionGroups = result.data.solutionGroups.map((group) => ({
      id: group.id,
      namespace: group.namespace,
      info: {
        name: group.info.name,
        description: group.info.description,
        publisherInfo: group.info.publisherInfo,
        logoUrl: group.info.logoUrl || '',
      },
      operatorsConfig: {
        startBlock: group.operatorsConfig.startBlock.toString(),
        maxOperatorWorkers: group.operatorsConfig.maxOperatorWorkers.toString(),
        allowedOperators: group.operatorsConfig.allowedOperators.toString(),
        hasOperatorsAllowlist: group.operatorsConfig.hasOperatorsAllowlist,
        stakingAmounts: {
          min: group.operatorsConfig.stakingAmounts.min,
          max: group.operatorsConfig.stakingAmounts.max,
        },
      },
      rewardsConfig: {
        id: group.rewardsConfig.id,
        subscriptionRewardPerBlock: group.rewardsConfig.subscriptionRewardPerBlock,
        votingRewardPerBlock: group.rewardsConfig.votingRewardPerBlock,
        topPerformanceBonus: group.rewardsConfig.topPerformanceBonus,
      },
      withdrawalDelay: group.withdrawalDelay ?? 0,
      operationStartBlock: group.operationStartBlock.toString(),
      operationEndBlock: group.operationEndBlock.toString(),
    }));

    return solutionGroups ?? [];
  };

  getSolutionGroup = async (id: string): Promise<PalletSolutionGroup> => {
    const query = graphql(`
      query GetSolutionGroup($id: String!) {
        solutionGroupById(id: $id) {
          id
          withdrawalDelay
          operationStartBlock
          operationEndBlock
          namespace
          info {
            description
            id
            logoUrl
            name
            publisherInfo
          }
          operatorsConfig {
            allowedOperators
            hasOperatorsAllowlist
            id
            maxOperatorWorkers
            stakingAmounts {
              id
              max
              min
            }
            startBlock
          }
          registrar {
            friendlyName
            id
            isAllowed
            legalLocation
          }
          rewardsConfig {
            id
            subscriptionRewardPerBlock
            topPerformanceBonus
            votingRewardPerBlock
          }
          solutions {
            id
            status
            namespace
            additionToExtraneousGroupsAllowed
            expirationBlock
            workload {
              workLogic
              executionEnvironment
            }
            expirationBlock
            info {
              id
              description
              name
            }
          }
        }
      }
    `);

    const result = await this.client.query(query, { id });
    if (!result.data || !result.data.solutionGroupById) {
      throw new Error('Failed to fetch solution groups');
    }

    const group = result.data.solutionGroupById;
    const solutions = group.solutions.map(
      ({
        workload: { executionEnvironment, workLogic },
        id,
        status,
        expirationBlock,
        info: { name },
      }) => ({
        name,
        flowSource: '',
        workLogic,
        id,
        creationDate: new Date(),
        environment: executionEnvironment,
        isDeleted: false,
        isExpired: false,
        actionStatus: 'NoAction',
        actionRetry: 0,
        isHadDeniedNode: false,
        downloadPath: null,
        updatedDate: null,
        status: status ?? null,
        statusDate: null,
        noderedId: null,
        ewxAddress: null,
        subcribeId: null,
        solutionId: null,
        actionErrorMsg: null,
        expirationBlock,
      }),
    );

    const solutionGroup = {
      id: group.id,
      namespace: group.namespace,
      info: {
        name: group.info.name,
        description: group.info.description,
        publisherInfo: group.info.publisherInfo,
        logoUrl: group.info.logoUrl || '',
      },
      operatorsConfig: {
        startBlock: group.operatorsConfig.startBlock.toString(),
        maxOperatorWorkers: group.operatorsConfig.maxOperatorWorkers.toString(),
        allowedOperators: group.operatorsConfig.allowedOperators.toString(),
        hasOperatorsAllowlist: group.operatorsConfig.hasOperatorsAllowlist,
        stakingAmounts: {
          min: group.operatorsConfig.stakingAmounts.min,
          max: group.operatorsConfig.stakingAmounts.max,
        },
      },
      rewardsConfig: {
        id: group.rewardsConfig.id,
        subscriptionRewardPerBlock: group.rewardsConfig.subscriptionRewardPerBlock,
        votingRewardPerBlock: group.rewardsConfig.votingRewardPerBlock,
        topPerformanceBonus: group.rewardsConfig.topPerformanceBonus,
      },
      operationStartBlock: group.operationStartBlock.toString(),
      operationEndBlock: group.operationEndBlock.toString(),
      withdrawalDelay: group.withdrawalDelay ?? 0,
      solutions,
    };
    return solutionGroup;
  };

  checkIsStaked = async (solutionNamespace: string, address: string): Promise<boolean> => {
    const query = graphql(`
      query GetSubscriptionById($solutionNamespace: String!, $address: String!) {
        operatorAccounts(where: { id_eq: $address }) {
          id
          subscriptions(where: { namespace_eq: $solutionNamespace }) {
            namespace
          }
        }
      }
    `);

    const result = await this.client.query(query, { solutionNamespace, address });

    if (!result.data || !result.data.operatorAccounts) {
      throw new Error('Failed to fetch operator account');
    }

    if (
      result.data.operatorAccounts.length === 0 ||
      result.data.operatorAccounts[0].subscriptions.length === 0
    ) {
      return false;
    }

    return true;
  };

  getLinkedWorkerNode = async (operatorId: string) => {
    const query = graphql(`
      query GetLinkedWorkerNode($operatorId: String!) {
        workerAccounts(where: { mapping: { operator: { id_eq: $operatorId } } }) {
          address
        }
      }
    `);
    return (
      (await this.client.query(query, { operatorId })).data?.workerAccounts.at(0)?.address ?? null
    );
  };

  getUnclaimedRewards = async (operatorId: string) => {
    const query = graphql(`
      query GetUnclaimedRewards($operatorId: String!) {
        unclaimedRewards(address: $operatorId) {
          subscriptionReward
          participationReward
          solutionGroupId
          address
        }
      }
    `);
    return (await this.client.query(query, { operatorId })).data?.unclaimedRewards ?? [];
  };

  getEwtUsd = async () => {
    const currency = 'EWT-USD';

    const query = graphql(`
      query GetEwtUsd($currency: String!) {
        ewtPriceById(id: $currency) {
          price
        }
      }
    `);

    return (await this.client.query(query, { currency })).data?.ewtPriceById?.price;
  };

  getSolutions = async () => {
    const query = graphql(`
      query GetSolutions {
        solutions {
          id
          status
          solutionGroup {
            id
          }
          info {
            description
            id
            logoUrl
            name
            publisherInfo
          }
          workload {
            workLogic
            id
            executionEnvironment
          }
          consensus {
            id
            maxWaitingThreshold
            voteThresholdPercent
          }
          expirationBlock
          additionToExtraneousGroupsAllowed
          nominationsEnabled
          namespace
        }
      }
    `);

    try {
      const q = await this.client.query(query, {});

      // Throw error if GraphQL query failed
      if (q.error) {
        const errorInfo = {
          errorMessage: q.error.message,
          networkError: q.error.networkError?.message,
          graphQLErrors: q.error.graphQLErrors?.map((err) => err.message),
          indexerUrl: this.url,
        };

        this.logger.error('[Indexer][getSolutions] GraphQL error occurred:', errorInfo);
        throw new Error(`GraphQL query failed: ${q.error.message}`);
      }

      const data = q.data;
      if (!data || !data.solutions) {
        this.logger.error('[Indexer][getSolutions] No data returned from indexer');
        throw new Error('No data returned from indexer');
      }

      const result = data.solutions;
      const solutions = result.map((solution) => ({
        data: {
          ...solution,
          consensus: {
            ...solution.consensus,
            voteThresholdPercent: solution.consensus.voteThresholdPercent.toString(),
            maxWaitingThreshold: solution.consensus.maxWaitingThreshold.toString(),
          },
        },
        id: solution.id,
        group: solution.solutionGroup?.id,
      }));

      return solutions;
    } catch (error: any) {
      this.logger.error('[Indexer][getSolutions] Unexpected error during GraphQL query:', {
        errorMessage: error.message,
        indexerUrl: this.url,
      });

      // Re-throw to let scheduler handle the abort
      throw error;
    }
  };

  getCurrentRewardPeriod = async () => {
    const query = graphql(`
      query GetCurrentRewardPeriod {
        activeRewardPeriodInfos(orderBy: index_DESC, limit: 1) {
          index
        }
      }
    `);

    const result = await this.client.query(query, {});

    if (!result.data || !result.data.activeRewardPeriodInfos) {
      throw new Error(
        "Failed to fetch current reward period - 'activeRewardPeriodInfos' is not defined",
      );
    }

    const item = result.data.activeRewardPeriodInfos.at(0);

    if (!item) {
      throw new Error('Failed to fetch current reward period - array is empty');
    }

    return item.index;
  };

  getScheduledUnsubscription = async (
    address: string,
    groupId: string,
    currentBlockNumber: number,
  ) => {
    const query = graphql(`
      query GetScheduledUnsubscription(
        $address: String!
        $groupId: String!
        $currentBlockNumber: Int!
      ) {
        unsubscriptionSchedules(
          where: {
            operator: { id_eq: $address }
            solutionGroup: { id_eq: $groupId }
            unsubscribeBlock_gte: $currentBlockNumber
          }
        ) {
          unsubscribeBlock
        }
      }
    `);

    const result = await this.client.query(query, { address, groupId, currentBlockNumber });
    const unsubscriptionSchedules = result?.data?.unsubscriptionSchedules;

    if (!unsubscriptionSchedules || unsubscriptionSchedules.length === 0) {
      return null;
    }

    const unsubscriptionBlock = unsubscriptionSchedules[0].unsubscribeBlock;
    return unsubscriptionBlock;
  };

  // TODO: These voting methods will be moved to VotesIndexer once it's implemented
  // For now, they remain here to maintain compatibility
  checkIsNodeReadyToVote = async (operatorId: string, solutionNamespace: string) => {
    const query = graphql(`
      query GetCanOperatorWorkerVote($operatorId: String!, $solutionNamespace: String!) {
        canOperatorWorkerVote(address: $operatorId, solutionGroupId: $solutionNamespace) {
          canVote
        }
      }
    `);

    const result = await this.client.query(query, { operatorId, solutionNamespace });

    if (result.data?.canOperatorWorkerVote?.length) {
      return result.data.canOperatorWorkerVote[0].canVote;
    }
    return false;
  };

  checkIsOperatorNodesReadyToVote = async (operatorId: string) => {
    const query = graphql(`
      query GetCanOperatorWorkersVote($operatorId: String!) {
        canOperatorWorkerVote(address: $operatorId) {
          solutionGroupId
          canVote
        }
      }
    `);

    return (await this.client.query(query, { operatorId })).data?.canOperatorWorkerVote ?? [];
  };

  syncVoteByWorkerAddress = async (address: string, rewardPeriod: string) => {
    // const query = graphql(`
    //   query syncVoteByWorkerAddress($address: String!, $rewardPeriod: String!) {
    //     workerAccountById(id: $address) {
    //       address
    //       submittedResults(where: { rewardPeriodIndex: { id_eq: $rewardPeriod } }) {
    //         rewardPeriodIndex {
    //           index
    //         }
    //         solution {
    //           namespace
    //         }
    //         solutionGroup {
    //           namespace
    //         }
    //         votingRoundId
    //         operator {
    //           id
    //         }
    //         worker {
    //           address
    //         }
    //       }
    //     }
    //   }
    // `);

    // const result = await this.client.query(query, { address, rewardPeriod });

    // if (result.error) {
    //   throw new Error(result.error.message);
    // }

    // return result.data;
    return {
      workerAccountById: {
        submittedResults: undefined,
      },
    };
  };
}
